import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CharacterStyleCandidate, CharacterStyleResult, CharacterStyleResultRole } from '../../../types';
import { tosThumbnail } from '../../../utils/tosImage';
import { Lightbox } from './Lightbox';
import { Tooltip } from './Tooltip';

const FAILED_TOOLTIP_MAX_WIDTH = 280;

interface CharacterStyleHistoryGridProps {
  results: CharacterStyleResult[];
  castingCandidates?: CharacterStyleCandidate[];
  allStylingCandidates?: Record<string, CharacterStyleCandidate[]>;
  onSetRole: (resultId: string, role: CharacterStyleResultRole) => void;
  onUpload: (file: File) => void;
}

interface ResultGroup {
  castingImageHash: string;
  stylingImageHash: string;
  castingImageUrl?: string;
  stylingImageUrl?: string;
  results: CharacterStyleResult[];
}

function fallbackToOriginalImage(
  event: React.SyntheticEvent<HTMLImageElement>,
  originalUrl: string,
  onFinalError: (img: HTMLImageElement) => void,
) {
  const img = event.currentTarget;
  if (img.dataset.fallbackApplied !== 'true' && img.getAttribute('src') !== originalUrl) {
    img.dataset.fallbackApplied = 'true';
    img.style.opacity = '1';
    img.style.display = 'block';
    img.src = originalUrl;
    return;
  }
  onFinalError(img);
}

export default function CharacterStyleHistoryGrid({
  results,
  castingCandidates = [],
  allStylingCandidates = {},
  onSetRole,
  onUpload,
}: CharacterStyleHistoryGridProps) {
  const { t } = useTranslation('projectDetail');
  const [previewIndex, setPreviewIndex] = useState<number>(-1);
  const [activeResultImageCount, setActiveResultImageCount] = useState(1);

  const allStylingFlat = useMemo(() => {
    const all: CharacterStyleCandidate[] = [];
    for (const candidates of Object.values(allStylingCandidates)) {
      all.push(...candidates);
    }
    return all;
  }, [allStylingCandidates]);

  const groups = useMemo(() => {
    const map = new Map<string, ResultGroup>();
    for (const result of results) {
      const key = `${result.castingImageHash}\x00${result.stylingImageHash}`;
      if (!map.has(key)) {
        const castingCandidate = castingCandidates.find(c => c.imageHash === result.castingImageHash);
        const stylingCandidate = allStylingFlat.find(c => c.imageHash === result.stylingImageHash);
        map.set(key, {
          castingImageHash: result.castingImageHash,
          stylingImageHash: result.stylingImageHash,
          castingImageUrl: castingCandidate?.imageUrl,
          stylingImageUrl: stylingCandidate?.imageUrl,
          results: [],
        });
      }
      map.get(key)!.results.push(result);
    }
    return Array.from(map.values());
  }, [results, castingCandidates, allStylingFlat]);

  const allPreviewUrls = useMemo(() => {
    const urls: string[] = [];
    for (const group of groups) {
      if (group.castingImageUrl) urls.push(group.castingImageUrl);
      if (group.stylingImageUrl) urls.push(group.stylingImageUrl);
      for (const r of group.results) {
        if (r.imageUrl && r.status !== 'pending' && r.status !== 'running' && r.status !== 'generating' && r.status !== 'failed') {
          urls.push(r.imageUrl);
        }
      }
    }
    return urls;
  }, [groups]);

  const loadableResultIds = useMemo(() => {
    const ids: string[] = [];
    for (const group of groups) {
      for (const result of group.results) {
        if (result.imageUrl && result.status !== 'pending' && result.status !== 'running' && result.status !== 'generating' && result.status !== 'failed') {
          ids.push(result.id);
        }
      }
    }
    return ids;
  }, [groups]);

  const loadableResultIdsKey = loadableResultIds.join('\x00');

  useEffect(() => {
    setActiveResultImageCount(loadableResultIds.length > 0 ? 1 : 0);
  }, [loadableResultIdsKey, loadableResultIds.length]);

  const openPreview = (url: string) => {
    const idx = allPreviewUrls.indexOf(url);
    setPreviewIndex(idx >= 0 ? idx : 0);
  };

  const advanceResultImageQueue = (resultId: string) => {
    const index = loadableResultIds.indexOf(resultId);
    if (index < 0) return;
    setActiveResultImageCount(count => Math.max(count, index + 2));
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>

        {groups.length === 0 && (
          <div style={{ minHeight: '80px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: '0.8rem' }}>
            {t('characterStyle.noResults')}
          </div>
        )}

        {groups.map(group => (
          <div key={`${group.castingImageHash}\x00${group.stylingImageHash}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
              <div
                onClick={() => group.castingImageUrl && openPreview(group.castingImageUrl)}
                style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', border: '2px solid #f59e0b', overflow: 'hidden', background: 'var(--surface-2)', flexShrink: 0, cursor: group.castingImageUrl ? 'zoom-in' : 'default' }}
              >
                {group.castingImageUrl ? (
                  <img src={tosThumbnail(group.castingImageUrl, 64)} alt={t('characterStyle.casting')} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => fallbackToOriginalImage(e, group.castingImageUrl!, img => { img.style.display = 'none'; })} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: 'var(--text-3)' }}>C</div>
                )}
              </div>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>+</span>
              <div
                onClick={() => group.stylingImageUrl && openPreview(group.stylingImageUrl)}
                style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', border: '2px solid #7c3aed', overflow: 'hidden', background: 'var(--surface-2)', flexShrink: 0, cursor: group.stylingImageUrl ? 'zoom-in' : 'default' }}
              >
                {group.stylingImageUrl ? (
                  <img src={tosThumbnail(group.stylingImageUrl, 64)} alt={t('characterStyle.styling')} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => fallbackToOriginalImage(e, group.stylingImageUrl!, img => { img.style.display = 'none'; })} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: 'var(--text-3)' }}>S</div>
                )}
              </div>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginLeft: 'var(--space-xs)' }}>{group.results.length}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 'var(--space-sm)' }}>
              {group.results.map((result, index) => {
                const isBusy = result.status === 'pending' || result.status === 'running' || result.status === 'generating';
                const isFailed = result.status === 'failed';
                const failedContent = isFailed ? (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)', color: 'var(--danger)', fontSize: '0.75rem', padding: 'var(--space-sm)', textAlign: 'center', background: 'var(--danger-light)' }}>
                    <span style={{ fontWeight: 500 }}>{t('characterStyle.failed')}</span>
                  </div>
                ) : null;
                return (
                  <div
                    key={result.id}
                    style={{ aspectRatio: '1', border: result.role === 'primary' ? '2px solid var(--primary)' : '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', background: 'var(--surface-2)', position: 'relative', transition: 'box-shadow var(--transition-fast)' }}
                  >
                    {isBusy && (
                      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)', color: 'var(--text-3)', fontSize: '0.7rem' }}>
                        <span className="loading-spinner" />
                        {t('characterStyle.loading')}
                      </div>
                    )}
                    {isFailed && (result.errorMsg
                      ? <Tooltip text={result.errorMsg} block maxWidth={FAILED_TOOLTIP_MAX_WIDTH}>{failedContent}</Tooltip>
                      : failedContent)}
                    {!isBusy && !isFailed && (
                      <>
                        <button
                          type="button"
                          onClick={() => openPreview(result.imageUrl)}
                          aria-label={t('characterStyle.previewResult', { n: index + 1 })}
                          style={{ width: '100%', height: '100%', padding: 0, border: 'none', background: 'none', display: 'block', cursor: 'zoom-in' }}
                        >
                          <img
                            src={(() => {
                              const loadIndex = loadableResultIds.indexOf(result.id);
                              return loadIndex >= 0 && loadIndex < activeResultImageCount ? tosThumbnail(result.imageUrl, 280) : '';
                            })()}
                            alt={t('characterStyle.resultAlt')}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            onLoad={() => advanceResultImageQueue(result.id)}
                            onError={e => fallbackToOriginalImage(e, result.imageUrl, img => {
                              img.style.opacity = '0.15';
                              advanceResultImageQueue(result.id);
                            })}
                          />
                        </button>
                        {(result.role === 'primary' || result.role === 'alternate') && (
                          <span style={{ position: 'absolute', top: 6, left: 6, background: result.role === 'primary' ? 'var(--primary)' : '#f59e0b', color: '#fff', borderRadius: 'var(--radius-sm)', padding: '2px 6px', fontSize: '0.6rem', fontWeight: 500, zIndex: 2 }}>
                            {result.role === 'primary' ? t('characterStyle.primaryBadge') : t('characterStyle.alternateBadge')}
                          </span>
                        )}
                        {result.role !== 'primary' && (
                          <button
                            type="button"
                            onClick={() => onSetRole(result.id, result.role === 'alternate' ? 'none' : 'alternate')}
                            title={t('characterStyle.alternate')}
                            aria-pressed={result.role === 'alternate'}
                            style={{ position: 'absolute', top: 6, right: 34, background: result.role === 'alternate' ? 'var(--primary)' : 'rgba(0,0,0,0.5)', color: '#fff', border: result.role === 'alternate' ? '2px solid #fff' : '2px solid rgba(255,255,255,0.6)', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', zIndex: 1 }}
                          >
                            {result.role === 'alternate' ? '★' : '☆'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onSetRole(result.id, result.role === 'primary' ? 'none' : 'primary')}
                          title={t('characterStyle.primary')}
                          aria-pressed={result.role === 'primary'}
                          style={{ position: 'absolute', top: 6, right: 6, background: result.role === 'primary' ? 'var(--primary)' : 'rgba(0,0,0,0.5)', color: '#fff', border: result.role === 'primary' ? '2px solid #fff' : '2px solid rgba(255,255,255,0.6)', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', zIndex: 1 }}
                        >
                          {result.role === 'primary' ? '✓' : '○'}
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {previewIndex >= 0 && ReactDOM.createPortal(
        <Lightbox urls={allPreviewUrls} index={previewIndex} onClose={() => setPreviewIndex(-1)} onChange={(idx) => setPreviewIndex(idx)} />,
        document.body,
      )}
    </>
  );
}
